/**
 * The From / To fields are LOCAL calendar dates, read as local midnight and
 * local end of day (challengeWindowInstants). Their defaults must come from the
 * same local calendar: built from the UTC date (toISOString), the default From
 * was TOMORROW for anyone west of UTC late in the evening (in New York after
 * 20:00), so a fresh challenge was "upcoming" and today's attempts did not
 * count.
 *
 * "Local" is an explicit IANA time zone (default: the browser's own), so the
 * calendar and the instants are computed in ONE zone, DST included, and the
 * behaviour is testable in any zone regardless of the process's TZ.
 */

/** The browser's IANA time zone. */
export function localTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

interface Parts { y: number; m: number; d: number; h: number; mi: number; s: number }

function partsIn(utcMs: number, tz: string): Parts {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const p: Record<string, number> = {};
  for (const { type, value } of fmt.formatToParts(new Date(utcMs))) {
    if (type !== 'literal') p[type] = Number(value);
  }
  return { y: p.year, m: p.month, d: p.day, h: p.hour === 24 ? 0 : p.hour, mi: p.minute, s: p.second };
}

/** Milliseconds the zone is ahead of UTC at that instant. */
function offsetMs(utcMs: number, tz: string): number {
  const q = partsIn(utcMs, tz);
  return Date.UTC(q.y, q.m - 1, q.d, q.h, q.mi, q.s) - Math.floor(utcMs / 1000) * 1000;
}

/** The UTC instant of a wall-clock time in the zone (DST-correct). */
function zonedToUtc(y: number, m: number, d: number, h: number, mi: number, s: number, tz: string): number {
  const asUtc = Date.UTC(y, m - 1, d, h, mi, s);
  let t = asUtc - offsetMs(asUtc, tz);
  t = asUtc - offsetMs(t, tz);
  return t;
}

const ymd = (y: number, m: number, d: number) =>
  `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

/** The calendar date (YYYY-MM-DD) of an instant in the zone. */
export function localDate(d: Date, tz: string = localTimeZone()): string {
  const p = partsIn(d.getTime(), tz);
  return ymd(p.y, p.m, p.d);
}

/** The default window: today through `days` days from today, on the zone's calendar. */
export function defaultChallengeWindow(
  now: Date = new Date(),
  days = 14,
  tz: string = localTimeZone()
): { startsAt: string; endsAt: string } {
  const p = partsIn(now.getTime(), tz);
  const end = new Date(Date.UTC(p.y, p.m - 1, p.d + days));
  return { startsAt: ymd(p.y, p.m, p.d), endsAt: ymd(end.getUTCFullYear(), end.getUTCMonth() + 1, end.getUTCDate()) };
}

/** The instants sent to the server: midnight of From through 23:59:59 of To, in the zone. */
export function challengeWindowInstants(
  startsAt: string,
  endsAt: string,
  tz: string = localTimeZone()
): { startsAt: string; endsAt: string } {
  const [sy, sm, sd] = startsAt.split('-').map(Number);
  const [ey, em, ed] = endsAt.split('-').map(Number);
  return {
    startsAt: new Date(zonedToUtc(sy, sm, sd, 0, 0, 0, tz)).toISOString(),
    endsAt: new Date(zonedToUtc(ey, em, ed, 23, 59, 59, tz)).toISOString(),
  };
}
